import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { AuthField } from "@/components/auth/AuthLayout";
import { BrandButton } from "@/components/PageHeader";
import { Pick } from "@/components/event-setup/BasicsSection";
import { CUSTOM_TYPES, TYPE_LABEL, hasOptions, newKey } from "@/lib/formOps";

const BLANK = { key: "", label: "", type: "text", builtin: false, required: false, help: "", placeholder: "", options: [], width: "" };

const Toggle = ({ label, checked, onChange, disabled, testId }) => (
  <label className={`flex items-center justify-between gap-3 rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm ${disabled ? "opacity-60" : ""}`}>
    {label}<Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testId} />
  </label>
);

export const FieldDialog = ({ state, sections, locked, onSave, onClose }) => {
  const [f, setF] = useState(BLANK);
  const [opts, setOpts] = useState("");
  const [si, setSi] = useState("0");
  useEffect(() => {
    if (!state) { return; }
    const base = state.field || { ...BLANK, key: newKey() };
    setF(base);
    setOpts((base.options || []).join("\n"));
    setSi(String(state.si));
  }, [state]);
  const isLocked = locked.includes(f.key);
  const set = (p) => setF((s) => ({ ...s, ...p }));
  const save = () => {
    const options = opts.split("\n").map((o) => o.trim()).filter(Boolean);
    if (!f.label.trim()) return toast.error("Give the field a label");
    if (hasOptions(f.type) && !options.length) return toast.error("Add at least one option, one per line");
    onSave(Number(si), { ...f, label: f.label.trim(), required: f.required || isLocked, options: hasOptions(f.type) ? options : [] }, state.field?.key);
  };
  return (
    <Dialog open={!!state} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="field-dialog">
        <DialogHeader><DialogTitle className="font-heading">{state?.field ? "Edit field" : "Add a new field"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {f.builtin && <p className="text-xs text-gray-500 flex items-center gap-1.5" data-testid="field-dialog-builtin-note">{isLocked && <Lock className="w-3.5 h-3.5" />}{f.type === "member_id" ? "Membership ID: used to check Good Standing. Members must enter it or tick 'I don't have one', so it always stays required. It can be renamed or moved, but not removed, and members cannot change it after sign-up." : isLocked ? "Core field: it can be renamed and moved, but always stays required." : "Built-in field: the type is fixed, but you can rename it, move it or make it optional."}</p>}
          <AuthField label="Label shown to members"><Input value={f.label} onChange={(e) => set({ label: e.target.value })} className="h-11" data-testid="field-label-input" /></AuthField>
          <AuthField label="Field type">
            {f.builtin ? <Input value={TYPE_LABEL[f.type] || f.type} disabled className="h-11" data-testid="field-type-readonly" />
              : <Pick value={f.type} onChange={(v) => set({ type: v })} options={CUSTOM_TYPES} testId="field-type-select" className="h-11" />}
          </AuthField>
          {hasOptions(f.type) && <AuthField label="Options (one per line)"><Textarea value={opts} onChange={(e) => setOpts(e.target.value)} className="min-h-[110px]" placeholder={"Option 1\nOption 2"} data-testid="field-options-input" /></AuthField>}
          <AuthField label="Section"><Pick value={si} onChange={setSi} options={sections.map((s, i) => ({ value: String(i), label: s.title || `Section ${i + 1}` }))} testId="field-section-select" className="h-11" /></AuthField>
          <AuthField label="Help text (optional)"><Input value={f.help} onChange={(e) => set({ help: e.target.value })} className="h-11" data-testid="field-help-input" /></AuthField>
          {!["checkbox", "multi", "select", "sex", "region", "title"].includes(f.type) && <AuthField label="Placeholder (optional)"><Input value={f.placeholder} onChange={(e) => set({ placeholder: e.target.value })} className="h-11" data-testid="field-placeholder-input" /></AuthField>}
          <Toggle label={isLocked ? "Required (always, core field)" : "Required"} checked={f.required || isLocked} onChange={(v) => set({ required: v })} disabled={isLocked} testId="field-required-switch" />
          <Toggle label="Use the full width of the row" checked={f.width === "full"} onChange={(v) => set({ width: v ? "full" : "" })} testId="field-width-switch" />
          <BrandButton onClick={save} className="w-full h-11" data-testid="field-dialog-save">{state?.field ? "Update field" : "Add field"}</BrandButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};
